/**
 * v2 (Project Independence) display formatting helpers (DNKADM-0049).
 *
 * The v2 scales (handoff §4.5): money is `DECIMAL(10,2)` in the **tenant
 * currency** (ISO-4217 — NEVER assume `$`); credits are `DECIMAL(8,2)`; cap hours
 * are `DECIMAL(5,1)`. These pure helpers render each at the correct scale. The
 * currency CODE is a required argument here — the tenant-currency SOURCE (a
 * `useTenantCurrency()` context) is 0050's job (this spec's Non-Goals); a feature
 * passes the code it already holds.
 *
 * All three are pure and side-effect-free; unit-tested for scale + currency
 * correctness (incl. a non-USD code).
 */

/**
 * Format a money amount (a plain decimal, e.g. `49` or `49.5`) in the given
 * ISO-4217 `currencyCode`, using the platform `Intl.NumberFormat` currency mode.
 * The currency symbol/placement is driven by the code — we NEVER hard-code `$`.
 * The locale is left to the runtime default so the symbol/grouping match the
 * environment; the currency itself is fixed by `currencyCode`.
 */
export function formatMoney(amount: number, currencyCode: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);
}

/**
 * The number of MINOR units per major unit for an ISO-4217 currency (mirrors the
 * payments screen's `minorUnitFactor`): 1 for zero-decimal currencies (JPY, KRW,
 * …), 1000 for the three-decimal Gulf currencies (BHD, KWD, …), else 100. Used to
 * convert a backend minor-unit amount (e.g. cents) to/from the major unit.
 */
export function minorUnitFactor(currencyCode: string): number {
  const c = currencyCode.trim().toUpperCase();
  if (
    ['BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'].includes(c)
  ) {
    return 1;
  }
  if (['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND'].includes(c)) {
    return 1000;
  }
  return 100;
}

/**
 * Format a MINOR-unit money amount (e.g. `2500` cents) in the given ISO-4217
 * `currencyCode`: divide by the currency's {@link minorUnitFactor} to the major
 * unit, then delegate to {@link formatMoney}. E.g. `formatMinorMoney(2500, 'USD')`
 * → `"$25.00"`; JPY (factor 1) → `formatMinorMoney(2500, 'JPY')` → `"¥2,500"`.
 */
export function formatMinorMoney(amountMinor: number, currencyCode: string): string {
  return formatMoney(amountMinor / minorUnitFactor(currencyCode), currencyCode);
}

/**
 * Format a credit amount (`DECIMAL(8,2)`) — always two decimal places, no
 * currency symbol (credits are not money). E.g. `12` → `"12.00"`, `12.5` →
 * `"12.50"`.
 */
export function formatCredits(n: number): string {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * "<amount> <bucket> credit(s)" (DNKADM-0454): the one place a credit count is worded, so a
 * bucket whose name already ends in "Credit"/"Credits" never reads "0.5 Lesson Credits credits".
 * The amount shows up to two decimals without trailing zeros (1 -> "1", 0.5 -> "0.5"). A name
 * that already ends in credit(s) keeps its own stem and case and only takes the singular/plural
 * ending ("1 Lesson Credit", "2 Lesson Credits"); any other name gets "credit"/"credits"
 * appended ("2 Lessons credits"); no name is "1 credit" / "2 credits". The count is 1 only
 * for exactly 1 (0.5 and 0 are plural).
 */
export function formatCreditAmount(n: number, bucketName?: string | null): string {
  const amount = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n);
  const plural = n !== 1;
  const name = bucketName?.trim() ?? '';
  const own = /^(.*?)\s*(credit)s?$/i.exec(name);
  if (own) {
    const stem = (own[1] ?? '').trim();
    const word = (own[2] ?? 'credit').slice(0, 1) + 'redit';
    const noun = plural ? `${word}s` : word;
    return `${amount} ${stem ? `${stem} ` : ''}${noun}`;
  }
  const noun = plural ? 'credits' : 'credit';
  return `${amount} ${name ? `${name} ` : ''}${noun}`;
}

/**
 * Format a cap-hours amount (`DECIMAL(5,1)`) — always one decimal place. E.g.
 * `5` → `"5.0"`, `1.5` → `"1.5"`.
 */
export function formatHours(n: number): string {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(n);
}
